import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, Sparkles, X } from "lucide-react";
import "./ChatWidget.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHIPS = ["What is a SIP?", "How can I save tax?", "How do I start investing?"];

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Arvi, Arvinder's AI assistant. Ask me anything about SIPs, mutual funds or tax savings." },
  ]);
  const sessionRef = useRef(localStorage.getItem("arvi_session") || null);
  const bodyRef = useRef(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const patchLast = (content) => setMessages(prev => {
    const copy = [...prev];
    copy[copy.length - 1] = { role: "assistant", content };
    return copy;
  });

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput("");
    setLoading(true);
    setMessages(prev => [...prev, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionRef.current }),
      });
      const sid = res.headers.get("X-Session-Id");
      if (sid) { sessionRef.current = sid; localStorage.setItem("arvi_session", sid); }
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages(prev => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      patchLast("Sorry, I couldn't respond right now. Please reach Arvinder directly on WhatsApp at +91 97282 90009.");
    } finally {
      setLoading(false);
    }
  };

  return <>
    <AnimatePresence>
      {open && (
        <motion.div
          className="chat-panel"
          data-testid="chat-panel"
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.96 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          <div className="chat-head">
            <div className="chat-avatar"><Sparkles size={16} /></div>
            <div className="chat-head-copy"><b>Ask Arvi</b><small>AI assistant · Swastik Financial</small></div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-button" aria-label="Close chat"><X size={17} /></button>
          </div>
          <div className="chat-body" ref={bodyRef} data-testid="chat-messages" data-lenis-prevent>
            {messages.map((m, i) => (
              <div key={i} className={`chat-msg ${m.role}`} data-testid={`chat-message-${i}`}>
                {m.content}
                {loading && i === messages.length - 1 && m.role === "assistant" && !m.content && (
                  <span className="chat-typing"><i /><i /><i /></span>
                )}
              </div>
            ))}
          </div>
          <div className="chat-chips">
            {CHIPS.map((c, i) => <button key={c} onClick={() => send(c)} disabled={loading} data-testid={`chat-chip-${i + 1}`}>{c}</button>)}
          </div>
          <form className="chat-input-row" onSubmit={e => { e.preventDefault(); send(); }}>
            <input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask about SIPs, tax savings..." data-testid="chat-input" />
            <button type="submit" disabled={loading || !input.trim()} data-testid="chat-send-button" aria-label="Send message"><Send size={16} /></button>
          </form>
          <small className="chat-note">Illustrative guidance only · Mutual fund investments are subject to market risk</small>
        </motion.div>
      )}
    </AnimatePresence>
    <button className="chat-fab" onClick={() => setOpen(!open)} data-testid="chat-widget-button" aria-label="Open AI chat assistant">
      {open ? <X size={20} /> : <MessageCircle size={20} />}
    </button>
  </>;
}
